import { MovementBlock, movementBlockFromError } from "components/Modals/MovementBlocker";
import { GridSelectionModel } from "@mui/x-data-grid-pro";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { api } from "services/api.service";
import { bulkEditError } from "utils/bulkEditError";
import { Inventory } from "types/inventory";
import { useAlert } from "./useAlert";
import { useConfirmation } from "./useConfirmation";

export type MoveRow = {
  id: string;
  kind: "inventory" | "location";
  recordId: number;
  kindLabel: string;
  label: string | null;
  pickup: { id: number; uuid: string } | null;
  lineId: string | number | null;
  sku: string | null;
  deviceType: string | null;
  manufacturer: string | null;
  securityLevel: string | null;
  enrolledStatus: string | null;
  warehouseLocation: string | null;
  locationId: number | null;
  serialNumber: string | null;
  inventoryImages: unknown[];
  /** Why this row cannot move to the last destination that was checked. */
  moveFailure?: string;
};

export type ScannedLocation = {
  id: number;
  code: string;
  name: string | null;
  locationTypeId: number | null;
  locationType: { id: number; name: string } | null;
  parentId: number | null;
  movable: boolean;
  path: string;
};

type ScanResult = {
  kind: "inventory" | "location";
  code: string;
  inventory?: { id: number };
  location?: ScannedLocation | Record<string, never> | null;
};

type MoveCheckFailure = {
  kind: "inventory" | "container" | "location";
  id: number;
  label: string;
  message: string;
  blocker?: MovementBlock;
};

type MoveCheckResult = {
  destination: ScanResult;
  failures: MoveCheckFailure[];
};

// The check calls a movable location a container. Rows only distinguish assets from locations.
function checkedRowKind(kind: MoveCheckFailure["kind"]): MoveRow["kind"] {
  return kind === "container" ? "location" : kind;
}

const blank = "---";

function inventoryRow(inventory: Inventory): MoveRow {
  return {
    id: `inventory-${inventory.id}`,
    kind: "inventory",
    recordId: inventory.id,
    kindLabel: "Asset",
    label: inventory.label ?? null,
    pickup: inventory.pickup ? { id: inventory.pickup.id, uuid: inventory.pickup.uuid } : null,
    lineId: inventory.lineId ?? null,
    sku: inventory.sku ?? null,
    deviceType: inventory.deviceType ?? null,
    manufacturer: inventory.manufacturer ?? null,
    securityLevel: inventory.securityLevel ?? null,
    enrolledStatus: inventory.enrolledStatus ?? null,
    warehouseLocation: inventory.warehouseLocation ?? null,
    locationId: inventory.locationId ?? null,
    serialNumber: inventory.serialNumber ?? null,
    inventoryImages: inventory.inventoryImages ?? [],
  };
}

function locationRow(location: ScannedLocation): MoveRow {
  const inside = location.path.split(" / ").slice(0, -1).join(" / ");
  return {
    id: `location-${location.id}`,
    kind: "location",
    recordId: location.id,
    kindLabel: location.locationType?.name ?? "Location",
    label: location.code,
    pickup: null,
    lineId: null,
    sku: null,
    deviceType: null,
    manufacturer: null,
    securityLevel: null,
    enrolledStatus: null,
    warehouseLocation: inside || null,
    locationId: location.parentId,
    serialNumber: null,
    inventoryImages: [],
  };
}

function savedLocation(scan: ScanResult | null): ScannedLocation | null {
  const location = scan?.kind === "location" ? scan.location : null;
  return location && "id" in location ? (location as ScannedLocation) : null;
}

function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } }).response?.status;
}

export function errorText(error: unknown, fallback: string): string {
  const errors = (error as { response?: { data?: { errors?: unknown } } }).response?.data?.errors;
  if (Array.isArray(errors) && errors.every((message) => typeof message === "string")) return errors.join(" ");
  if (errors && typeof errors === "object") {
    const message = (errors as { message?: unknown }).message;
    if (typeof message === "string") return message;
    const messages = Object.values(errors as Record<string, unknown>)
      .flat()
      .filter((value): value is string => typeof value === "string");
    if (messages.length > 0) return messages.join(" ");
  }
  return fallback;
}

function count(items: number, noun = "item"): string {
  return `${items} ${items === 1 ? noun : `${noun}s`}`;
}

function destinationText(value: unknown): string {
  return String(value ?? "").trim();
}

export function display(value: unknown): string {
  if (value === null || value === undefined || value === "") return blank;
  return String(value);
}

export default function useMoveItems() {
  const { search } = useLocation();
  const { alertSuccess, alertError, alertInfo } = useAlert();
  const { confirm } = useConfirmation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const [rows, setRows] = useState<MoveRow[]>([]);
  const [selected, setSelected] = useState<GridSelectionModel>([]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // Formik is created below, after this callback. The ref lets a finished move clear the field.
  const movingRef = useRef(false);
  const moveGeneration = useRef(0);
  const resetDestinationRef = useRef<() => void>(() => undefined);
  const [moving, setMoving] = useState(false);
  const [blocker, setBlocker] = useState<MovementBlock | null>(null);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const checkingRef = useRef(false);

  const beginMove = useCallback(() => {
    moveGeneration.current += 1;
    movingRef.current = true;
    setMoving(true);
    return moveGeneration.current;
  }, []);

  const finishMove = useCallback((generation: number) => {
    if (moveGeneration.current !== generation) return;
    movingRef.current = false;
    setMoving(false);
  }, []);

  const remember = useCallback(
    (row: MoveRow) => {
      if (rowsRef.current.some((existing) => existing.id === row.id)) {
        alertInfo(`${row.kindLabel} ${row.label || row.recordId} is already added.`);
        return;
      }
      setRows((current) => [row, ...current]);
      setSelected((current) => [...current, row.id]);
    },
    [alertInfo],
  );

  const resolve = useCallback(async (code: string): Promise<ScanResult | null> => {
    try {
      const { data } = await api.get<ScanResult>("/scan", { params: { code } });
      return data;
    } catch (error) {
      if (httpStatus(error) === 404) return null;
      throw error;
    }
  }, []);

  const addCode = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code || blockerRef.current) return;
      let scan: ScanResult | null;
      try {
        scan = await resolve(code);
      } catch (error) {
        alertError(errorText(error, `Couldn't look up ${code}.`));
        return;
      }
      if (!scan) {
        alertError(`Can't find an asset or location for ${code}.`);
        return;
      }
      if (scan.kind === "location") {
        const location = savedLocation(scan);
        if (!location) {
          alertError(`${scan.code} isn't a saved location yet, so it can't be moved.`);
          return;
        }
        if (!location.movable) {
          alertError(`${location.code} is a fixed location and can't be moved.`);
          return;
        }
        remember(locationRow(location));
        return;
      }
      if (!scan.inventory) return;
      try {
        const { data } = await api.get<Inventory>(`/inventories/${scan.inventory.id}`);
        remember(inventoryRow(data));
      } catch (error) {
        alertError(errorText(error, `Couldn't load asset ${scan.inventory.id}.`));
      }
    },
    [alertError, remember, resolve],
  );

  const removeRow = useCallback((rowId: string) => {
    setRows((current) => current.filter((row) => row.id !== rowId));
    setSelected((current) => current.filter((id) => id !== rowId));
  }, []);

  const drop = useCallback((ids: string[]) => {
    setRows((current) => current.filter((row) => !ids.includes(row.id)));
    setSelected((current) => current.filter((id) => !ids.includes(String(id))));
  }, []);

  // Each location moves on its own, so one refusal does not stop the others. Rows that moved leave the list;
  // rows that did not stay, and the reasons come back for the caller to report.
  const moveLocations = useCallback(
    async (locations: MoveRow[], destination: ScannedLocation | null, code: string) => {
      const moved: string[] = [];
      const failures: string[] = [];
      if (locations.length === 0) return { moved, failures };
      if (!destination) {
        failures.push(`${code} isn't a saved location yet, so locations can't be moved into it.`);
        return { moved, failures };
      }
      for (const location of locations) {
        try {
          await api.put(`/location_management/locations/${location.recordId}`, {
            location: { parentId: destination.id },
          });
          moved.push(location.id);
        } catch (error) {
          failures.push(errorText(error, `Couldn't move ${location.label} into ${destination.code}.`));
        }
      }
      drop(moved);
      return { moved, failures };
    },
    [drop],
  );

  const moveToLocation = useCallback(
    (
      items: MoveRow[],
      code: string,
      destination: ScannedLocation | null,
      skipActions: Record<number, string[]> = {},
    ) => {
      const inventories = items.filter((item) => item.kind === "inventory");
      const locations = items.filter((item) => item.kind === "location");
      const payload = inventories.map((inventory) => ({
        id: inventory.recordId,
        // The move is the location name. The server looks that name up and stores the matching id.
        warehouseLocation: code,
        ...(skipActions[inventory.recordId] || []).reduce<Record<string, boolean>>((acc, flag) => {
          acc[flag] = true;
          return acc;
        }, {}),
      }));
      const request =
        inventories.length === 0 ? Promise.resolve() : api.put("inventories/bulk/edit", { inventories: payload });
      const generation = beginMove();
      return request
        .then(async () => {
          if (inventories.length > 0) drop(inventories.map((inventory) => inventory.id));
          const { moved, failures } = await moveLocations(locations, destination, code);
          if (failures.length === 0) {
            // Leave the scanned destination in place until the move lands, so a failed save or a
            // cancelled skip can be tried again without scanning the location a second time.
            resetDestinationRef.current();
            alertSuccess(`Moved ${count(items.length)} to ${code}.`);
            return;
          }
          // Assets and some locations may already be at the destination, so the message says how far the move got.
          const movedCount = inventories.length + moved.length;
          const summary = movedCount > 0 ? `Moved ${movedCount} of ${count(items.length)} to ${code}. ` : "";
          alertError(`${summary}${failures.join(" ")}`);
        })
        .catch((error) => {
          const block = movementBlockFromError(error);
          if (block) {
            setBlocker(block);
            return;
          }
          const skip = bulkEditError(error);
          if (skip.status === 422 && skip.param && skip.id !== undefined) {
            const { id, param } = skip;
            // The dialog keeps its last text, so the server's question replaces the move question.
            confirm({
              message: skip.message ?? `Move ${count(items.length)} to ${code} anyway?`,
              context: param,
              handler: () =>
                moveToLocation(items, code, destination, {
                  ...skipActions,
                  [id]: [...(skipActions[id] || []), param],
                }),
            });
            return;
          }
          alertError(errorText(error, `Couldn't move items to ${code}.`));
        })
        .finally(() => finishMove(generation));
    },
    [alertError, alertSuccess, beginMove, confirm, drop, finishMove, moveLocations],
  );

  const { handleSubmit, getFieldProps, errors, resetForm, setFieldValue } = useFormik({
    initialValues: { destination: destinationText(query.get("warehouse_location")) },
    validationSchema: Yup.object({ destination: Yup.string().trim().required("Scan a location") }),
    onSubmit: async (values) => {
      if (movingRef.current || checkingRef.current || blockerRef.current) return;
      const typed = destinationText(values.destination);
      const items = rowsRef.current.filter((row) => selected.includes(row.id));
      if (items.length === 0) return;
      if (items.some((row) => row.moveFailure)) return;

      let check: MoveCheckResult;
      checkingRef.current = true;
      try {
        const { data } = await api.post<MoveCheckResult>("/scan/move_check", {
          destination: typed,
          inventoryIds: items.filter((row) => row.kind === "inventory").map((row) => row.recordId),
          locationIds: items.filter((row) => row.kind === "location").map((row) => row.recordId),
        });
        check = data;
      } catch (error) {
        alertError(errorText(error, `Couldn't look up ${typed}.`));
        return;
      } finally {
        checkingRef.current = false;
      }

      if (check.failures.length > 0) {
        const messages = new Map(
          check.failures.map((failure) => [`${checkedRowKind(failure.kind)}-${failure.id}`, failure.message]),
        );
        setRows((current) =>
          current.map((row) => ({ ...row, moveFailure: messages.get(`${row.kind}-${row.recordId}`) })),
        );
        const block = check.failures.find((failure) => failure.blocker)?.blocker;
        if (block) setBlocker(block);
        else alertError(check.failures.map((failure) => `${failure.label}: ${failure.message}`).join("\n"));
        return;
      }

      const destination = savedLocation(check.destination);
      const code = check.destination.code;
      confirm({
        message: `Move ${count(items.length)} to "${code}"?`,
        handler: () => moveToLocation(items, code, destination),
      });
    },
  });
  resetDestinationRef.current = resetForm;

  const handleExScanInput = useCallback(
    (
      event: { target: { value: string }; persist: () => void },
      key: string,
      formikHandler: (event: unknown) => void,
    ) => {
      event.persist();
      const value = event.target.value;
      try {
        const url = new URL(value);
        event.target.value = url.searchParams.get(key) ?? value;
        formikHandler(event);
      } catch {
        formikHandler(event);
      }
    },
    [],
  );

  useEffect(() => {
    const inventoryId = query.get("inventory_id");
    if (inventoryId) addCode(inventoryId);
    // The id comes from the address this page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const destinationField = getFieldProps("destination");
  const destinationProps = {
    ...destinationField,
    onChange: (event: Parameters<typeof destinationField.onChange>[0]) => {
      destinationField.onChange(event);
      const next = destinationText((event as { target: { value: string } }).target.value);
      if (next === destinationText(destinationField.value)) return;
      setRows((current) => current.map((row) => (row.moveFailure ? { ...row, moveFailure: undefined } : row)));
    },
  };

  return {
    blocker,
    closeBlocker: () => {
      setBlocker(null);
      void setFieldValue("destination", "", false);
      setRows((current) => current.map((row) => ({ ...row, moveFailure: undefined })));
    },
    destinationInputRef,
    rows,
    selected,
    setSelected,
    addCode,
    removeRow,
    handleExScanInput,
    submitForm: handleSubmit,
    errors,
    destinationProps,
    moving,
  };
}
